import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

interface FCMContextType {
  notifcationType: string | null;
  notificationCount: number;
  handleIncomingNotification: (data: any) => void;
  resetNotifcationType: () => void;
}

const FCMContext = createContext<FCMContextType | undefined>(undefined);

interface FCMProviderProps {
  children: React.ReactNode;
}

export const FCMProvider: React.FC<FCMProviderProps> = ({ children }) => {
  const [notifcationType, setNotifcationType] = useState<string | null>(null);
  const [notificationCount, setNotificationCount] = useState<number>(0);

  // CRITICAL FIX: Memoize to prevent infinite re-renders in Header useEffect
  // Without useCallback, this function changes every render, causing useEffect
  // with this dependency to run repeatedly (100+ times/second)
  const handleIncomingNotification = useCallback((data: any) => {
    setNotifcationType(data.type);
    setNotificationCount(data?.count);
  }, []);

  const resetNotifcationType = useCallback(() => {
    setNotifcationType(null);
  }, []);

  // Memoize the context value to prevent unnecessary re-renders
  const value: FCMContextType = useMemo(() => ({
    notifcationType,
    notificationCount,
    handleIncomingNotification,
    resetNotifcationType,
  }), [notifcationType, notificationCount, handleIncomingNotification, resetNotifcationType]);

  return <FCMContext.Provider value={value}>{children}</FCMContext.Provider>;
};

export const useFCM = (): FCMContextType => {
  const context = useContext(FCMContext);

  if (context === undefined) {
    throw new Error('useFCM must be used within an FCMProvider');
  }

  return context;
};

export default FCMContext;
